import React, { createContext, useState, useContext, useEffect } from 'react';
import { message } from 'antd';
import axios from 'axios';

const AuthContext = createContext(null);

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器 - 自动添加token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器 - 处理401错误
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // 只有在非登录请求时才重定向，避免登录失败时的无限重定向
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/shortdrama/login';
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 检查本地存储的登录状态
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // 验证token有效性
      checkAuth();
    }
    setLoading(false);
  }, []);

  const checkAuth = async () => {
    try {
      const response = await api.get('/auth/profile');
      setUser(response.data.user);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    } catch (error) {
      logout();
    }
  };

  const login = async (usernameOrEmail, password) => {
    try {
      const response = await api.post('/auth/login', {
        username: usernameOrEmail,
        password,
      });
      
      const { user, access_token } = response.data;
      setUser(user);
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      message.success('登录成功');
      return { success: true };
    } catch (error) {
      const errorMsg = error.response?.data?.error || '登录失败';
      // 检查是否是用户不存在的错误
      if (errorMsg.includes('用户不存在') || errorMsg.includes('用户名或密码错误') || error.response?.status === 401) {
        message.error('用户不存在或密码错误，建议您先注册');
      } else {
        message.error(errorMsg);
      }
      return { success: false, error: errorMsg };
    }
  };

  const register = async (username, email, phone, password, role = 'creator') => {
    try {
      const response = await api.post('/auth/register', {
        username,
        email,
        phone,
        password,
        role,
      });
      
      const { user, access_token } = response.data;
      setUser(user);
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      message.success('注册成功');
      return { success: true };
    } catch (error) {
      const errorMsg = error.response?.data?.error || '注册失败';
      message.error(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    message.success('已退出登录');
  };

  const updateProfile = async (data) => {
    try {
      const response = await api.put('/auth/profile', data);
      setUser(response.data.user);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      message.success('更新成功');
      return { success: true };
    } catch (error) {
      message.error('更新失败');
      return { success: false };
    }
  };

  const value = {
    user,
    setUser,
    loading,
    login,
    register,
    logout,
    updateProfile,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export { api };
